import mapboxgl from 'mapbox-gl'
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder'
import * as turf from '@turf/turf'
import { stores } from './stores'

// Make them globally available for your existing code
window.mapboxgl = mapboxgl
window.MapboxGeocoder = MapboxGeocoder
window.turf = turf

// ==========================================================================
// 🗺️ Map Functionality - Store Locator
// ==========================================================================

class StoreLocator {
  constructor() {
    this.mapboxToken =
      'pk.eyJ1IjoidHJlYm9ycG9zdCIsImEiOiJjbWVzbGlmcjcwM3NiMmxzZ2kxajdwdHZpIn0.g2bQoy-bvc0JQLelB33uHw'
    this.map = null
    this.origin = null
    this.init()
  }

  init() {
    // Set up map toggle functionality
    this.setupMapToggle()
  }

  setupMapToggle() {
    const stockistLink = document.querySelector('a[href="#find-a-stockists"]')
    const mapEl = document.querySelector('.locator')

    if (stockistLink && mapEl) {
      stockistLink.addEventListener('click', (e) => {
        e.preventDefault()
        e.stopPropagation()

        const isActive = mapEl.classList.contains('active')

        if (!isActive) {
          this.openMap()
        } else {
          this.closeMap()
        }
      })

      // Close map when clicking outside
      mapEl.addEventListener('click', (e) => {
        if (e.target === mapEl) {
          this.closeMap()
        }
      })

      // Close on escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && mapEl.classList.contains('active')) {
          this.closeMap()
        }
      })
    }
  }

  openMap() {
    const mapEl = document.querySelector('.locator')
    if (!mapEl) return

    // Lock body scroll
    document.body.classList.add('body--map-open')

    // Show map with transition
    mapEl.classList.add('active')

    // Initialize mapbox if not already done
    if (!this.map) {
      this.initializeMapbox()
    } else {
      // Resize map when reopening (important for iOS)
      setTimeout(() => {
        this.map.resize()
      }, 400) // Match transition duration
    }
  }

  closeMap() {
    const mapEl = document.querySelector('.locator')
    if (!mapEl) return

    // Unlock body scroll
    document.body.classList.remove('body--map-open')

    // Hide map
    mapEl.classList.remove('active')
  }

  initializeMapbox() {
    if (typeof mapboxgl === 'undefined' || !stores) {
      return
    }

    mapboxgl.accessToken = this.mapboxToken

    this.map = new mapboxgl.Map({
      container: 'map',
      style: 'mapbox://styles/mapbox/light-v11',
      center: [-6.2, 53.34],
      zoom: 12,
      preserveDrawingBuffer: true, // Fix for iOS
    })

    // Add stores IDs
    stores.features.forEach((store, i) => {
      store.properties.id = i
    })

    this.map.on('load', () => {
      // Force resize after map loads (iOS fix)
      setTimeout(() => {
        this.map.resize()
      }, 100)

      this.map.addSource('stores', {
        type: 'geojson',
        data: stores,
      })

      this.buildLocationList(stores)
      this.addMarkers()
      this.autoHighlightNearest()
      this.initGeocoder()
    })
  }

  siteUrl(url) {
    if (!url) return ''
    return /^https?:\/\//i.test(url) ? url : `http://${url}`
  }

  templateHTML(prop, isPopup = false) {
    const site = this.siteUrl(prop.website)
    const phone = prop.phone
    const dist =
      typeof prop.distanceKm === 'number'
        ? `<span class="store__distance t-sm">${prop.distanceKm.toFixed(1)} km</span>`
        : ''
    const title = isPopup
      ? `<h5 class="store__title">${prop.name} ${dist}</h5>`
      : `<div class="store__title" id="link-${prop.id}">${prop.name} ${dist}</div>`

    return `
      <div class="store" id="store-${prop.id}" data-tags="${(prop.tags || []).join(',')}">
        ${title}
        <div class="store__details">
          <div class="store__address">${prop.address}</div>
          <div class="store__links">
        ${phone ? `<a class="btn" href="tel:${phone}">Phone</a>` : ''}
        ${site ? `<a class="btn" href="${site}" target="_blank" rel="noopener">Website</a>` : ''}
          </div>
        </div>
      </div>
        `
  }

  flyToStore(feature) {
    console.log('Flying to store:', feature.properties.name, feature.geometry.coordinates)

    // First, easeTo the marker position with offset
    this.map.easeTo({
      center: feature.geometry.coordinates,
      zoom: 15,
      offset: [0, -150], // Offset upward to show popup below
      duration: 800,
    })
  }

  createPopUp(feature) {
    const existing = document.getElementsByClassName('mapboxgl-popup')
    if (existing[0]) existing[0].remove()

    const html = this.templateHTML(feature.properties, true)
    new mapboxgl.Popup({
      closeOnClick: false,
    })
      .setLngLat(feature.geometry.coordinates)
      .setHTML(html)
      .addTo(this.map)
  }

  setActive(id) {
    document
      .querySelectorAll('.listings .item')
      .forEach((n) => n.classList.remove('active'))
    const el = document.getElementById(`listing-${id}`)
    if (el) el.classList.add('active')
  }

  onItemActivate(el) {
    const id = el.dataset.id
    const f = stores.features.find(
      (x) => String(x.properties.id) === String(id)
    )
    this.setActive(id)
    this.flyToStore(f)
    this.createPopUp(f)
  }

  buildLocationList(storesObj) {
    const listings = document.getElementById('listings')
    if (!listings) return

    listings.innerHTML = ''
    for (const store of storesObj.features) {
      const prop = store.properties
      const html = `
        <div class="item" id="listing-${prop.id}" data-id="${prop.id}" role="button" tabindex="0">
          ${this.templateHTML(prop, false)}
        </div>
      `
      listings.insertAdjacentHTML('beforeend', html)

      const itemEl = document.getElementById(`listing-${prop.id}`)
      itemEl.addEventListener('click', (e) => {
        if (e.target.closest('.store__links a')) return
        this.onItemActivate(itemEl)
      })

      itemEl.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          this.onItemActivate(itemEl)
        }
      })
    }
  }

  addMarkers() {
    for (const feature of stores.features) {
      const el = document.createElement('div')
      el.id = `marker-${feature.properties.id}`
      el.className = 'marker'
      el.style.cursor = 'pointer'

      // Check if this is the Sandycove Martello Tower by name
      const name = feature.properties.name || ''
      const isCenterPoint =
        name.toLowerCase().includes('sandycove') &&
        name.toLowerCase().includes('martello')

      if (isCenterPoint) {
        el.className = 'marker marker--center'
      }

      const marker = new mapboxgl.Marker(el, {
        offset: [0, -28],
      })
        .setLngLat(feature.geometry.coordinates)
        .addTo(this.map)

      // Add click handler to both element and marker
      const handleClick = (e) => {
        if (e) {
          e.stopPropagation()
          e.preventDefault()
        }
        console.log('=== MARKER CLICKED ===')
        console.log('Feature:', feature.properties.name)
        console.log('Coordinates:', feature.geometry.coordinates)
        console.log('Map exists:', !!this.map)

        this.flyToStore(feature)
        setTimeout(() => {
          this.createPopUp(feature)
          this.setActive(feature.properties.id)
        }, 100)
      }

      el.addEventListener('click', handleClick)
      el.addEventListener('mousedown', (e) => {
        console.log('Mousedown on marker:', feature.properties.name)
      })
    }
  }

  setOriginFromLngLat(lng, lat) {
    this.origin = [lng, lat]
    this.updateDistancesAndRender()
  }

  updateDistancesAndRender() {
    if (!this.origin || !turf) return

    for (const f of stores.features) {
      const dKm = turf.distance(this.origin, f.geometry.coordinates, {
        units: 'kilometers',
      })
      f.properties.distanceKm = dKm
    }

    stores.features.sort(
      (a, b) =>
        (a.properties.distanceKm ?? 1e9) - (b.properties.distanceKm ?? 1e9)
    )
    this.buildLocationList(stores)

    const nearest = stores.features[0]
    if (nearest) {
      this.setActive(nearest.properties.id)
      this.flyToStore(nearest)
      this.createPopUp(nearest)
    }
  }

  autoHighlightNearest() {
    const fallbackToCenter = () => {
      const c = this.map.getCenter()
      this.setOriginFromLngLat(c.lng, c.lat)
    }

    if (!('geolocation' in navigator)) {
      fallbackToCenter()
      return
    }

    navigator.geolocation.getCurrentPosition(
      (pos) =>
        this.setOriginFromLngLat(pos.coords.longitude, pos.coords.latitude),
      () => fallbackToCenter(),
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 60000,
      }
    )
  }

  initGeocoder() {
    if (typeof MapboxGeocoder === 'undefined') return

    const geocoder = new MapboxGeocoder({
      accessToken: mapboxgl.accessToken,
      mapboxgl,
      marker: false,
      placeholder: 'Search by address…',
      bbox: [-10.5, 51.4, -5.4, 55.4], // Ireland bounds
      proximity: [-7.5, 53.5], // Center of Ireland
      types: 'country,region,place,postcode,locality,neighborhood,address,poi',
      filter: function (item) {
        return item
      },
    })

    const geocoderContainer = document.getElementById('geocoder')
    if (geocoderContainer) {
      geocoderContainer.appendChild(geocoder.onAdd(this.map))
      geocoder.on('result', (e) => {
        const c = e.result.center
        this.setOriginFromLngLat(c[0], c[1])
      })
    }
  }
}

// Initialize store locator when DOM is ready
function initStoreLocator() {
  if (document.querySelector('.locator')) {
    new StoreLocator()
  }
}

export { initStoreLocator, StoreLocator }
